<template>
  <ul :class="['list', listClass]">
    <li
      v-for="(card, i) in cards"
      :key="i"
      class="list_el"
      :class="[card.extraClass, { 'last-element': isLast(i) }]"
      :style="card.bgImage ? { backgroundImage: `url(${card.bgImage})` } : {}"
    >
      <a
        :href="card.href"
        v-bind="card.attrs"
        @click="card.onClick ? card.onClick($event) : undefined"
      >
        {{ card.label }}
        <span v-if="card.sublabel">{{ card.sublabel }}</span>
      </a>

      <select
        v-if="card.select"
        class="nav-card-select"
        :value="selectValues[i]"
        @click.stop
        @change="onSelectChange(i, $event.target.value)"
      >
        <option v-for="opt in card.select.options" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </li>
  </ul>
</template>

<script setup>
import { ref } from 'vue';

/**
 * NavCardList — used by:
 *   home.html          (4 cards: Learn / Practice / Visit / Explore)
 *   level_home.html    (10 numbered level cards)
 *   build_practice.html, transitionxart.html, transformation.html,
 *   vernatics.html, musicxart.html  (variable card sets, driven by page config)
 *
 * Each card object shape:
 * {
 *   href:       string          — anchor target
 *   label:      string          — main visible text
 *   sublabel?:  string          — text inside <span>
 *   bgImage?:   string          — imported image URL for background-image
 *   extraClass?: string         — additional li class
 *   attrs?:     object          — extra attrs spread onto <a> (e.g. onclick string was here before)
 *   onClick?:   Function        — click handler (replaces inline onclick strings)
 *   select?:    {               — optional dropdown rendered below the card's link
 *     value:    string          — initial selected value
 *     options:  {value, label}[]
 *     onChange: (value) => void — called with the newly selected value
 *   }
 * }
 */
const props = defineProps({
  cards: {
    type: Array,
    required: true,
  },
  /** CSS class added to the <ul> alongside 'list' */
  listClass: {
    type: String,
    default: 'homepage-list',
  },
  /** Index of the last card (for last-element class). Defaults to last in array. */
  lastIndex: {
    type: Number,
    default: null,
  },
});

const isLast = (i) => {
  const last = props.lastIndex !== null ? props.lastIndex : props.cards.length - 1;
  return i === last;
};

const selectValues = ref(props.cards.map((card) => card.select?.value ?? null));

const onSelectChange = (i, value) => {
  selectValues.value[i] = value;
  props.cards[i].select?.onChange?.(value);
};
</script>
